import { SERVICE_TEETH_MAPPING_REVERSE } from "../constants/orderForms";

export const FDItoUTNMapping = {
  18: 1,
  17: 2,
  16: 3,
  15: 4,
  14: 5,
  13: 6,
  12: 7,
  11: 8,
  21: 9,
  22: 10,
  23: 11,
  24: 12,
  25: 13,
  26: 14,
  27: 15,
  28: 16,
  48: 32,
  47: 31,
  46: 30,
  45: 29,
  44: 28,
  43: 27,
  42: 26,
  41: 25,
  31: 24,
  32: 23,
  33: 22,
  34: 21,
  35: 20,
  36: 19,
  37: 18,
  38: 17,
};

export const UTNtoFDIMapping = {
  1: 18,
  2: 17,
  3: 16,
  4: 15,
  5: 14,
  6: 13,
  7: 12,
  8: 11,
  9: 21,
  10: 22,
  11: 23,
  12: 24,
  13: 25,
  14: 26,
  15: 27,
  16: 28,
  32: 48,
  31: 47,
  30: 46,
  29: 45,
  28: 44,
  27: 43,
  26: 42,
  25: 41,
  24: 31,
  23: 32,
  22: 33,
  21: 34,
  20: 35,
  19: 36,
  18: 37,
  17: 38,
};

export const updateTeethMapping = (values, notation) => {
  if (notation === "FDI") {
    const newValues = values?.map((d) => UTNtoFDIMapping[d]);
    return newValues;
  } else if (notation === "UTN") {
    const newValues = values?.map((d) => FDItoUTNMapping[d]);
    return newValues;
  }
  return values;
};

// Tooth selections are stored as FDI internally; notation conversion is display-only.
export const toDisplayedTeeth = (values = [], notation) =>
  (values || []).map((value) =>
    notation === "UTN" ? FDItoUTNMapping[value] ?? value : value
  );

const SUMMARY_TOOTH_FIELDS = [
  "restorationSites",
  "implantSites",
  "teethNotToMove",
  "cutForDiesTeeth",
  "waxupSimulationTeeth",
  "recontouringGuidesTeeth",
  "deprogrammerTeeth",
  "teethNeedToExtract",
  "implantSite",
  "splintExtendToTeeth",
  "dentureSetupTeeth",
  "partialDentureSetupTeeth",
  "noClaspDesignTeeth",
  "graftingSiteTeeth",
  "endodonticAccessTeeth",
  "implantSiteTeeth",
  "densahBursTeeth",
];

const SUMMARY_SCALAR_FIELDS = new Set([
  'preferredSoftware', 'preferredDesignSoftware', 'processType',
  'needDesignPreview', 'designFileNeeded', 'constructionFileNeeded',
  'printableModelRequired', 'printableModelincludeDies', 'restorationLacksOC',
  'needHandle', 'holesInImpressionTray', 'needArticulationAttachments',
  'needDrainHoles', 'needEngravedText', 'nightguardProcessing',
  'densahBursUsed', 'densahBurGuideDesired', 'flapBeRaised', 'guidanceLevel',
  'conversionDentureAvailable', 'digitalConversionDenture', 'prosthesisBeUsed',
  'midline', 'teethNotToMoveOption', 'printableAligners', 'options',
  'archesToTreat', 'molarOcclusalGoals', 'ipr', 'overjetCorrectionOption',
  'overjetCorrectionOptionImproveLength', 'overbiteCorrectionOption',
  'overbiteCorrectionOptionImproveLength', 'crossbiteCorrection',
  'attachmentsAllowed', 'attachmentsAllowedOn', 'retainerRequired',
  'archNightguardOn', 'softTissueReductionGuideFor', 'hardTissueReductionGuideFor',
  'finalLevelDistance', 'recontouringGuideProcessing', 'preferredDeprogrammerDesign',
  'archDeprorammerOn', 'deprogrammerThickness', 'deprogrammerProcessing',
  'preferredSplintDesign', 'archSplintOn', 'splintThickness', 'splintProcessing',
  'dentureBaseProcessing', 'teethProcessing', 'monoblocPrintDesired',
  'biteRegistrationDesired', 'copyDentureDesired', 'occlusionSetup', 'toothMouldShape',
  'teethSetupStyle', 'teethSize', 'crossbiteCreated', 'dentureForPhotoScan',
  'intraoralScansFor', 'digitalInversionFor', 'modelFile', 'baseThickness',
  'needTwoPrintableModelFile', 'needPrintableModelFile', 'needPrintableEggshellFiles',
  'nightguardThickness', 'needEngravedText', 'drainHolesNeeded', 'articulationAttachmentsNeeded',
  'guidedAccessDrillBrand', 'drillShankDiameter', 'preferredImplantCompany', 'implantLine',
  'preferredPinCompany', 'pinLine', 'implantSurgicalKit', 'surgeryDate', 'abutmentPreferences',
  'boneReductionInPlan', 'guidanceLevel',
]);

export const mergeActiveServiceOption = (options = {}, option) => {
  if (!option?.type || option?.expiryDate != null) return options;
  const values = options[option.type];
  if (SUMMARY_SCALAR_FIELDS.has(option.type)) {
    options[option.type] = option.optionstring;
  } else {
    options[option.type] = [...(Array.isArray(values) ? values : []), option.optionstring];
  }
  return options;
};

export const normalizeSummaryServices = (services, notation) =>
  (Array.isArray(services) ? services : [])
    .filter((service) => service && service.expiryDate == null)
    .map((service) => {
      const fields = {};
      (service.serviceTooth || []).forEach((item) => {
        const field = SERVICE_TEETH_MAPPING_REVERSE[item?.type];
        if (field && item.expiryDate == null && item.teethno != null) {
          fields[field] = [...(fields[field] || []), item.teethno];
        }
      });
      (service.serviceOptions || []).forEach((item) => {
        if (item?.type && item.expiryDate == null && item.optionstring != null) {
          fields[item.type] = [...(fields[item.type] || []), item.optionstring];
        }
      });
      // Explicit draft fields, including empty arrays, override historical nested rows.
      const normalized = { ...fields, ...service };
      Object.entries(normalized).forEach(([field, value]) => {
        if (Array.isArray(value) && SUMMARY_TOOTH_FIELDS.includes(field)) {
          normalized[field] = toDisplayedTeeth([...new Set(value)], notation);
        } else if (Array.isArray(value) && field in fields && field !== 'serviceTooth') {
          normalized[field] = [...new Set(value)];
        }
        if (SUMMARY_SCALAR_FIELDS.has(field) && Array.isArray(normalized[field])) {
          const answers = [...new Set(normalized[field])];
          // Conflicting answers cannot be ordered safely without persistence metadata.
          normalized[field] = answers.length === 1 ? answers[0] : undefined;
        }
      });
      normalized.preferredSoftware = normalized.preferredSoftware ?? normalized.preferredDesignSoftware;
      return normalized;
    });

export const getSummaryDisplayValue = (value, fallback = "Unavailable") => {
  if (value == null || (typeof value === "string" && !value.trim()) ||
      (Array.isArray(value) && !value.length)) return fallback;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return value;
};

export const getSummaryListDisplayValue = (value, fallback = "Unavailable") => {
  if (!Array.isArray(value) || value.length === 0) return fallback;
  return value.filter((item) => item != null && String(item).trim() !== "").join(", ") || fallback;
};

// Normalize only display data. Redux state and API payloads remain canonical FDI.
export const normalizeSummaryToothDisplay = (data = {}) => {
  const source = data.caseDetails ?? data;
  const notation = source.numberSystem ?? data.numberSystem;
  const readTeeth = (selected, rows) => selected ?? (rows || [])
    .filter((item) => item?.expiryDate == null)
    .map((item) => item.teethno);
  const display = (values) => toDisplayedTeeth([...new Set(values || [])], notation);
  const missing = readTeeth(source.missingTeethSelectedTeeth,
    source.missingTeeth ?? data.missingTeeth);
  const services = normalizeSummaryServices(source.services ?? data.services, notation);
  return {
    ...data,
    services,
    caseDetails: {
      ...source,
      numberSystem: notation,
      hasMissingTeeth: source.hasMissingTeeth ?? data.hasMissingTeeth ??
        (missing.length ? 'Yes' : null),
      missingTeethSelectedTeeth: display(missing),
      teethToExtractSelectedTeeth: display(readTeeth(source.teethToExtractSelectedTeeth,
        source.teethToExtractDet ?? data.teethToExtractDet)),
      anyTeethMobileSelectedTeeth: display(readTeeth(source.anyTeethMobileSelectedTeeth,
        source.anyTeethMobileDet ?? data.anyTeethMobileDet)),
      services,
    },
  };
};

export const scrollToTop = () => {
  window.scroll({
    top: 0,
    // behavior: "smooth",
  });
};

export const isBlankValue = (value) =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim() === "") ||
  (Array.isArray(value) && value.length === 0);

export const hasAnyTruthyValue = (value) =>
  Array.isArray(value) ? value.some(Boolean) : Boolean(value);

export const getBackendValue = (value) =>
  value === undefined || value === null ? "" : value;

export const getBackendYesNoValue = (value) => {
  if (value === undefined || value === null) {
    return "";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "string") {
    const normalized = value.trim();
    if (normalized === "") {
      return "";
    }

    if (normalized.toLowerCase() === "yes") {
      return "Yes";
    }

    if (normalized.toLowerCase() === "no") {
      return "No";
    }

    return normalized;
  }

  return value ? "Yes" : "No";
};

export const normalizeOrderServices = (services) =>
  Object.entries(services || {})
    .map(([serviceObjId, service]) => ({
      ...(service || {}),
      serviceObjId: service?.serviceObjId || serviceObjId,
    }))
    .filter((service) => service?.serviceObjId);

export const implantCompanyList = [
  "Biohorizons",
  "Biomet 3i",
  "Blue Sky Bio",
  "Bredent",
  "Camlog",
  "Dentium",
  "Dentsply Sirona",
  "Dio",
  "Hi Ossen",
  "Implant Direct",
  "Keystone",
  "MIS",
  "Megagen",
  "Neodent",
  "Neoss",
  "Nobel Biocare",
  "Osstem",
  "Ritter",
  "Straumann",
  "TRI Matrix",
  "TruAbutment",
  "ZimVie",
  "Other",
];

export const stackableGuideOptionsList1 = [
  "Multi unit abutment indexing or positioning guide",
  "Tissue spacer for temporary prosthesis pick up",
  "Provisional denture for conversion or backup",
  "Printable reduced jaw model",
  "Printable pre reduction jaw model",
  "Printable reduced jaw model with multi unit digital analog recesses",
  "Printable scalloped bone reduction guide for FP1 cases",
  "Printed metal base bone reduction guide",
  "Printed metal implant drill guide",
  "Printed metal scalloped bone reduction guide for FP1 cases",
];

export const stackableGuideOptionsList2 = [
  "Multi unit abutment indexing or positioning guide",
  "Tissue spacer for temporary prosthesis pick up",
  "Provisional denture for conversion or backup",
];

export const findExtraElement = (array1, array2) => {
  const sum1 = array1.reduce((acc, val) => acc + val, 0);
  const sum2 = array2.reduce((acc, val) => acc + val, 0);
  return sum2 - sum1;
};

export const getArrayDiff = (previousValues = [], nextValues = []) => {
  const previousSet = new Set(previousValues);
  const nextSet = new Set(nextValues);

  return {
    added: nextValues.filter((value) => !previousSet.has(value)),
    removed: previousValues.filter((value) => !nextSet.has(value)),
  };
};

export const MAP_LIBRARIES = ["places"];

export const checkObjects = (firstObj, secondObj) => {
  if (
    typeof firstObj !== "object" ||
    typeof secondObj !== "object" ||
    Object.keys(firstObj)?.length !== Object.keys(secondObj)?.length
  ) {
    return false;
  }

  let result = true;

  Object.keys(firstObj)?.forEach((key) => {
    if (firstObj?.[key] !== secondObj?.[key]) result = false;
  });

  return result;
};

export const isImageFormatDisplayable = (name) => {
  const DISPLAYABLE_FORMATS = ["jpeg", "jpg", "png", "gif", "webp", "avif"];
  const fileNameArray = name?.split(".");
  const fileFormat = fileNameArray[fileNameArray?.length - 1];
  if (DISPLAYABLE_FORMATS.includes(fileFormat?.toLowerCase())) {
    return true;
  }
  return false;
};

export const getServicePriceRange = (categories) => {
  const priceRange = {
    priceRangeFrom: 1000,
    priceRangeTo: 0,
  };

  categories.forEach((d) => {
    if (priceRange.priceRangeFrom > d?.priceRangeFrom) {
      priceRange.priceRangeFrom = d?.priceRangeFrom;
    }
    if (priceRange.priceRangeTo < d?.priceRangeTo) {
      priceRange.priceRangeTo = d?.priceRangeTo;
    }
  });

  return priceRange;
};
